import React from 'react';
import { Wifi, WifiOff, AlertCircle } from 'lucide-react';

interface RealtimeStatusProps {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
  error?: Error | null;
  className?: string;
  showDetails?: boolean;
}

export function RealtimeStatus({ 
  status, 
  error, 
  className = '',
  showDetails = false 
}: RealtimeStatusProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'connected':
        return {
          icon: <Wifi className="w-4 h-4" />,
          text: 'Connected',
          color: 'text-green-500',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          pulseColor: 'bg-green-400'
        };
      case 'connecting':
        return {
          icon: <Wifi className="w-4 h-4 animate-pulse" />,
          text: 'Connecting...',
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          pulseColor: 'bg-yellow-400'
        };
      case 'disconnected':
        return {
          icon: <WifiOff className="w-4 h-4" />,
          text: 'Disconnected',
          color: 'text-gray-500',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          pulseColor: 'bg-gray-400'
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          text: 'Connection Error',
          color: 'text-red-500',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          pulseColor: 'bg-red-400'
        };
    }
  };

  const config = getStatusConfig();

  if (!showDetails) {
    // Minimal indicator
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="relative">
          {status === 'connected' && (
            <span className={`absolute inset-0 rounded-full ${config.pulseColor} animate-ping opacity-20`} />
          )}
          <span className={`relative flex items-center justify-center w-2 h-2 rounded-full ${config.pulseColor}`} />
        </div>
        <span className={`text-xs ${config.color}`}>
          {config.text}
        </span>
      </div>
    );
  }

  // Detailed status card
  return (
    <div className={`rounded-lg border ${config.borderColor} ${config.bgColor} p-3 ${className}`}>
      <div className="flex items-center gap-2">
        <div className={config.color}>
          {config.icon}
        </div>
        <div className="flex-1">
          <p className={`text-sm font-medium ${config.color}`}>
            {config.text}
          </p>
          {error && (
            <p className="text-xs text-gray-600 mt-1">
              {error.message}
            </p>
          )}
        </div>
        {status === 'connected' && (
          <div className="relative">
            <span className={`absolute inset-0 rounded-full ${config.pulseColor} animate-ping opacity-30`} />
            <span className={`relative flex w-2 h-2 rounded-full ${config.pulseColor}`} />
          </div>
        )}
      </div>
      
      {showDetails && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Real-time updates {status === 'connected' ? 'active' : 'inactive'}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Hook to use with realtime status
 */
export function useRealtimeStatusMessage(
  status: 'connecting' | 'connected' | 'disconnected' | 'error'
): string {
  switch (status) {
    case 'connected':
      return 'Real-time updates are active';
    case 'connecting':
      return 'Establishing connection...';
    case 'disconnected':
      return 'Real-time updates paused';
    case 'error':
      return 'Connection issue detected';
    default:
      return '';
  }
}